<template>
  <CurrentEventDataProvider v-slot="{ data, logoUrl }">
    <div class="flex flex-wrap overflow-hidden w-full h-full md:space-y-2">
      <!-- Navbar -->
      <div
        class="navbar top-0 z-30 flex justify-between items-center text-center sticky font-bold bg-dark px-4 w-full"
      >
        <div class="flex items-center h-16">
          <img
            :src="'https://jolly-coast-0ed26f703-development.westeurope.3.azurestaticapps.net/ec7c06a311e758b602863004841e8d19.svg'"
            :alt="logoUrl && $t('Views.Login.eventLogo')"
            class="object-scale-down h-6 md:h-12 w-auto"
          />
        </div>
      </div>
      <!--  -->
      <div class="flex flex-row w-full h-full px-2 md:px-4">
        <!-- Sidebar -->
        <div v-if="showSlider" class="w-full md:w-1/4 flex flex-col">
          <div class="sidebar-container shadow-lg p-4 space-y-4 md:space-y-6">
            <!-- Event detail -->
            <div class="flex justify-between items-center w-full">
              <div class="shadow-md p-1 rounded-full">
                <img
                  :src="logoUrl"
                  :alt="logoUrl && $t('Views.Login.eventLogo')"
                  class="object-scale-down h-8 w-8"
                />
              </div>
              <h2 class="font-bold text-md">
                {{ data.eventName }}
              </h2>
              <i class="fas fa-chevron-down ml-2" aria-hidden="true" />
            </div>
            <!-- Filters refresh-->
            <div
              class="flex flex-col md:flex-row justify-between md:items-center w-full"
            >
              <!-- Filters hidden for small screen-->
              <h2
                class="hidden md:flex font-bold text-base pt-2 md:pt-0 items-center"
              >
                {{ $t('Views.PollResults.filters') }}
              </h2>
              <div class="flex w-full justify-between md:justify-end">
                <TappinButton
                  :disabled="loading"
                  :icon="loading ? 'circle-notch' : 'repeat'"
                  @click="refreshItems"
                  class="button button-secondary text-center items-center"
                  :aria-label="
                    loading
                      ? $t('Views.PollResults.loading')
                      : $t('Views.PollResults.refresh')
                  "
                  :custom_caption="
                    loading ? '' : $t('Views.PollResults.refresh')
                  "
                />
                <TappinButton
                  v-if="showSlider"
                  :icon="'close'"
                  @click="toggleSlider"
                  class="inline md:hidden whitespace-nowrap button button-secondary"
                  :aria-label="$t('Views.PollResults.closeFilter')"
                  :custom_caption="$t('Views.PollResults.closeFilter')"
                />
              </div>
              <!-- Filters hidden for large screen-->
              <h2 class="md:hidden font-bold text-base pt-2 md:pt-0">
                {{ $t('Views.PollResults.filters') }}
              </h2>
            </div>
            <!-- Date picker -->
            <div class="flex flex-col gap-2">
              <h2 class="font-bold text-base">
                {{ $t('Views.PollResults.date') }}
              </h2>
              <Filters
                showDates
                @date-filter="handleDates"
                @checkbox-filter="handleCheckBoxFilters"
              />
            </div>
            <!--Select template-->
            <div
              v-if="!showStream && !pollAccessKey"
              class="flex flex-col gap-2"
            >
              <label for="generic-template">{{
                $t('Views.PollResults.selectQuizGroup')
              }}</label>
              <div class="relative h-8 flex flex-wrap my-2">
                <input
                  type="text"
                  :placeholder="$t('Views.PollResults.searchHere')"
                  class="border border-black rounded-lg w-full"
                  @click="showSelectTemplate = true"
                  v-model="searchTemplateQuery"
                  @input="handleTemplateInput"
                />
                <div
                  class="absolute bottom-0 right-0 pr-4"
                  v-if="searchTemplateQuery !== ''"
                  @click="clearSearchInput"
                >
                  <i class="far fa-times" />
                </div>
              </div>
            </div>
            <!--Rating list-->
            <div
              v-if="!showStream && !pollAccessKey"
              class="flex flex-col gap-2"
            >
              <label for="generic-input">{{
                $t('Views.PollResults.selectQuiz')
              }}</label>
              <!-- Search input field -->
              <div class="relative h-8 flex flex-wrap my-2">
                <input
                  type="text"
                  v-model="searchQuery"
                  :placeholder="$t('Views.PollResults.searchHere')"
                  class="border border-black rounded-lg w-full"
                  @click="showSelect = true"
                  @input="handleInput"
                />
                <div
                  class="absolute bottom-0 right-0 pr-4"
                  v-if="searchQuery !== ''"
                  @click="clearQuizSearchInput"
                >
                  <i class="far fa-times" />
                </div>
              </div>
              <select
                v-show="showSelect"
                id="generic-input"
                v-model="selectedGenericPoll"
                @change="handleChangeQuiz"
                class="flex flex-col border border-black rounded-lg p-2 gap-2 w-full"
                size="5"
              >
                <option disabled value="">
                  {{ $t('Views.PollResults.selectQuiz') }}
                </option>
                <!-- Filtered options based on searchQuery -->
                <option
                  v-for="poll in filteredPolls"
                  :key="poll.id"
                  :value="poll"
                >
                  {{ poll.title }}
                </option>
              </select>
            </div>
            <!-- Group -->
            <div v-if="!groupId" class="flex flex-col justify-between gap-2">
              <div class="flex justify-between border-b border-grey p-2">
                <h2 class="font-bold text-base">
                  {{ $t('Components.Filters.groups') }}
                </h2>
                <i class="fas fa-chevron-down ml-2" aria-hidden="true" />
              </div>
              <Filters
                showCheckbox
                @checkbox-filter="handleCheckBoxFilters"
                :groups="groups"
                :tableCheckBoxName="$t('Components.Filters.groups')"
                showGroupModal
              />
            </div>
          </div>
        </div>
        <!-- Content -->
        <div
          :class="
            showSlider
              ? 'w-1/6 md:w-3/4 flex flex-col space-y-4 h-full md:ml-4'
              : 'w-full md:w-3/4 flex flex-col space-y-4 h-full md:ml-4'
          "
        >
          <!--loader-->
          <div v-if="loading" slot="footer" class="loading-spinner">
            <Spinner />
          </div>
          <div
            class="poll-results shadow-lg py-2 md:p-2 space-y-0"
            style="
              color: var(--login-color-text);
              background-color: var(--login-background-color);
            "
          >
            <!--poll content-->
            <div
              :class="[
                'custom-card-parent',
                'flex',
                'flex-col',
                'h-full',
                'bg-white',
                'text-dark',
                'justify-between',
                'py-2'
              ]"
            >
              <div class="flex flex-wrap justify-between sticky top-0 bg-white">
                <!--header and export-->
                <div class="flex space-x-2">
                  <div class="hidden md:flex" v-if="selectedPage !== null">
                    <TappinButton
                      :icon="loadingExport ? 'circle-notch' : ''"
                      class="button button-primary"
                      :disabled="loadingExport"
                      @click="handleExport"
                      :custom_caption="
                        loadingExport ? '' : $t('Views.PollResults.export')
                      "
                    />
                  </div>
                  <div class="button flex lg:hidden" @click="toggleSlider">
                    <TappinButton
                      v-if="!showSlider"
                      icon="filter"
                      class="button button-primary"
                      :custom_caption="$t('Views.PollResults.filters')"
                    />
                  </div>
                </div>
              </div>
              <div>
                <QuizList :polls="templates" :templateTitle="templateTitle" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </CurrentEventDataProvider>
</template>

<script>
import ApiClient from '@/client';
import { mapActions, mapGetters } from 'vuex';
import Filters from '@/components/shared/Filters.vue';
import Spinner from '@/components/shared/Spinner';
import TappinButton from '@/components/buttons/TappinButton.vue';
import { resultsPageType } from '@/constants/poll-type-constants';
import QuizList from './QuizList.vue';

export default {
  props: {
    pollAccessKey: String,
    groupId: String
  },
  components: {
    Spinner,
    Filters,
    TappinButton,
    QuizList
  },
  data() {
    return {
      loading: false,
      checkBoxFilter: [],
      groupsFilter: [],
      fromDate: '',
      toDate: '',
      loadingFilter: false,
      showSlider: false,
      dataTable: [],
      currentPoll: 0,
      registrations: [],
      searchQuery: '',
      searchTemplateQuery: '',
      selectedTemplate: null,
      selectedGenericPoll: null,
      showStream: false,
      showMultipleStream: false,
      showQuizList: false,
      showOpenTextManagement: false,
      showSelect: false,
      showSelectTemplate: false,
      templates: [],
      selectedAccessKey: null,
      selectedPage: resultsPageType.RESULTS,
      loadingExport: false,
      templateTitle: ''
    };
  },
  created() {
    this.fetchItems();
    this.fetchGroups();

    this.checkScreenSize();
    // Add an event listener to react to screen resize
    window.addEventListener('resize', this.checkScreenSize);
  },
  computed: {
    ...mapGetters('Groups', { groups: 'getItems' }),
    ...mapGetters('CurrentEvent', [{ eventName: 'getTitle' }]),
    ...mapGetters('CurrentEvent', [
      'getLocale',
      'getEvent',
      'isWebappEnabled',
      'isLobbyEnabled',
      'features'
    ])
  },
  methods: {
    ...mapActions('Polls', ['getPollResults']),
    ...mapActions('Groups', { fetchGroups: 'fetchItems' }),
    toggleSlider() {
      this.showSlider = !this.showSlider;
    },
    handleInput() {
      // Update filteredPolls based on searchQuery
      // Show the select element only if there's input in the search field
      this.showSelect = this.searchQuery.length > 0;
    },
    handleTemplateInput() {
      // Update filteredTemplates based on searchTempalteQuery
      // Show the select element only if there's input in the search field
      this.showSelectTemplate = this.searchTemplateQuery.length > 0;
    },
    handleDates(e) {
      this.fromDate = e.from;
      this.toDate = e.to;

      if (
        this.toDate ||
        (this.fromDate && this.toDate) ||
        (!this.fromDate && !this.toDate)
      ) {
        this.fetchPollResults();
      }
    },
    handleCheckBox(e) {
      const value = e.target.value.trim();
      const isChecked = this.checkBoxFilter.find((v) => v === value);
      if (!isChecked) {
        this.checkBoxFilter.push(value);
      } else {
        this.checkBoxFilter = this.checkBoxFilter.filter(
          (v) => v.trim() !== value
        );
      }
    },
    handleCheckBoxFilters(e) {
      this.groupsFilter = e;
      this.loadingFilter = true;
      this.fetchPollResults();
    },

    clearSearchInput() {
      this.searchTemplateQuery = '';
      this.showSelectTemplate = true;
    },

    clearQuizSearchInput() {
      this.searchQuery = '';
      this.showSelect = true; // Optional: hide select dropdown after clearing input
    },
    async fetchItems() {
      this.loading = true;
      this.fetchPollResults();
    },
    async fetchPollResults() {
      const response = await ApiClient.getTemplateResults(
        this.pollAccessKey,
        this.fromDate,
        this.toDate,
        this.groupsFilter
      );
      this.templates = response.data.templateResults;
      this.templateTitle = response.data.templateTitle;
      this.loading = false;
    },

    checkScreenSize() {
      // Use window.innerWidth to get the current viewport width
      // You can adjust the breakpoint value according to your requirements
      if (window.innerWidth >= 1024) {
        this.showSlider = true;
      } else {
        this.showSlider = false;
      }
    },
    refreshItems() {
      this.selectedTemplate = null;
      this.fetchItems();
    },
    async handleExport() {
      this.loadingExport = true;
      const res = ApiClient.exportTemplateResults(
        this.pollAccessKey,
        this.fromDate,
        this.toDate,
        this.groupsFilter
      );
      this.loadingExport = false;
    }
  },
  destroyed() {
    // Remove the event listener when the component is destroyed
    window.removeEventListener('resize', this.checkScreenSize);
  }
};
</script>

<style scoped lang="scss">
:root {
  --sidebar-height: 100%;
}
@media screen and (max-width: 1023px) {
  .slider {
    display: none;
    position: fixed;
    top: -100%; /* Initially hide the slider off-screen */
    left: 0;
    width: 100%;
    height: 100%;
    background-color: white;
    transition: top 0.5s; /* Add a smooth transition for opening and closing */
    z-index: 999; /* Ensure the slider is above other content */
  }

  .slider.open {
    top: 0; /* Move the slider to the top of the screen when open */
  }
}
.slider {
  display: flex;
}
.custom-card-parent .card {
  box-shadow: none;
}
.poll-results {
  height: calc(100vh - 5em);
  .card {
    .toggle-type {
      color: var(--color-secondary-bg);
      opacity: 0.75;
    }
  }
}
.sidebar-container {
  overflow-y: auto;
  height: calc(100vh - 5em);
}
</style>
